import { useEffect, useRef, useMemo, useCallback } from "react";
import { useVehiclesSocketStore } from "../store/admin/vehiclesSocketStore";
import { useVehicleFootprintTrackingStore } from "../store/admin/vehicleFootprintTrackingStore";
import { useCompaniesStore } from "../store/admin/companiesStore";
import { isPlatformAdminUser } from "./useIsReseller";
import { useGroupsStore } from "../store/admin/groupsStore";
import { connectSocket } from "../socket";
import { queryClient } from "../lib/queryClient";
import { allDevicesQueryOptions } from "../features/devices/queries";
import type { DashCam } from "../store/admin/devicesStore";

interface DeviceFromAPI {
  id: string;
  fullId?: string;
  identifier?: string;
  model?: string;
  status_id?: string;
  type?: string;
  company_id?: string;
  companyId?: string;
  vehicleId?: string;
  vehicle_id?: string;
  remolque_id?: string;
  serial?: string;
  sim_card?: string;
  brand_id?: string;
  haveDashCam?: boolean;
  dashCam?: DashCam | null;
  createdAt?: { _seconds: number; _nanoseconds: number };
  updatedAt?: { _seconds: number; _nanoseconds: number };
  vehicle?: {
    id?: string;
    brand?: string;
    model?: string;
    year?: number;
    plates?: string;
    plate?: string;
    color?: string;
    name?: string;
    type?: string;
    icon_id?: string | null;
  };
  driver?: {
    id?: string;
    firstName?: string;
    lastName?: string;
    license_id?: string;
    photo?: string;
    experience?: string;
    phone?: string;
    email?: string;
  };
  group?: string;
  icon?: string;
  imei?: string;
  lastPosition?: {
    lat?: number;
    lon?: number;
    speed?: number | null;
    heading?: number | null;
    timeStamp?: number;
    altitude?: number | null;
    accuracy?: number | null;
    battery?: number | null;
    ignition?: boolean;
    event_code?: number;
    params?: {
      engine_status?: number;
      event_code?: number;
      odo_km?: number;
    };
  };
}

export const fetchDevicesFromAPI = async (
  companyId?: string,
  forceRefresh = false,
) => {
  const resolvedCompanyId =
    companyId ||
    useVehiclesSocketStore.getState().selectedCompany ||
    useCompaniesStore.getState().companyId ||
    useCompaniesStore.getState().companyUser ||
    "";
  try {
    const raw = await queryClient.fetchQuery(
      forceRefresh
        ? { ...allDevicesQueryOptions(resolvedCompanyId || null), staleTime: 0 }
        : allDevicesQueryOptions(resolvedCompanyId || null),
    );
    const data = raw as unknown as DeviceFromAPI[];

    const normalizedData = (data || []).map((device: DeviceFromAPI) => ({
      id: device.id,
      fullId: device.fullId || device.identifier,
      identifier: device.identifier,
      companyId: device.companyId || device.company_id || resolvedCompanyId || "",
      vehicle_id: device.vehicle_id || device.vehicleId,
      remolque_id: device.remolque_id,
      brand_id: device.brand_id,
      
      model: device.model,
      status_id: device.status_id,
      type: device.type,
      serial: device.serial,
      sim_card: device.sim_card,
      imei: device.imei || device.identifier,
      haveDashCam: device.haveDashCam === true,
      dashCam: device.dashCam ?? null,
      // Dos equipos en el mismo camión: el que trae `paired_with` es el
      // secundario y no pinta marcador. Ver `pairedDeviceIds` en el store.
      paired_with: device.paired_with ?? null,

      vehicle: device.vehicle ? {
        ...device.vehicle,
        plates: device.vehicle.plates || device.vehicle.plate,
      } : undefined,

      driver: device.driver ? {
        id: device.driver.id,
        name: device.driver.firstName + " " + device.driver.lastName || "Sin asignar",
        licenseId: device.driver.license_id || "",
        photo: device.driver.photo || "",
        experience: device.driver.experience || "",
        phone: device.driver.phone || "",
        email: device.driver.email,
      } : undefined,

      group: device.group,
      icon: device.icon,
      icon_id: device.vehicle?.icon_id ?? null,

      lastPosition: device.lastPosition,
    }));

    return normalizedData;
  } catch (error) {
    console.error("Error fetching devices:", error);
    return [];
  }
};

export const useVehiclesSocketSubscription = () => {
  const vehicles = useVehiclesSocketStore((state) => state.vehicles);
  const loading = useVehiclesSocketStore((state) => state.loading);
  const isConnected = useVehiclesSocketStore((state) => state.isConnected);
  const allCompanyDeviceIds = useVehiclesSocketStore((state) => state.allCompanyDeviceIds);
  const initializeWithDevices = useVehiclesSocketStore((state) => state.initializeWithDevices);
  const connect = useVehiclesSocketStore((state) => state.connect);
  const disconnect = useVehiclesSocketStore((state) => state.disconnect);
  const resubscribe = useVehiclesSocketStore((state) => state.resubscribe);
  const setAwaitingCompanySelection = useVehiclesSocketStore((state) => state.setAwaitingCompanySelection);
  const setLoading = useVehiclesSocketStore((state) => state.setLoading);
  const resetStore = useVehiclesSocketStore((state) => state.resetStore);

  const selectedGroup = useVehiclesSocketStore((state) => state.selectedGroup);
  const selectedCompany = useVehiclesSocketStore((state) => state.selectedCompany);

  const companyId = useCompaniesStore((state) => state.companyId);

  const groups = useGroupsStore((state) => state.groups);

  // Footprint tracking
  const activeFootprintIds = useVehicleFootprintTrackingStore((state) =>
    Object.keys(state.activeFootprints).sort().join(',')
  );
  const addFootprintPoint = useVehicleFootprintTrackingStore((state) => state.addFootprintPoint);

  const vehiclesRef = useRef(vehicles);
  vehiclesRef.current = vehicles;

  const hasHandledInit = useRef(false);
  const previousGroupRef = useRef<string>(
    useVehiclesSocketStore.getState().selectedGroup
  );
  const previousCompanyRef = useRef<string | null>(null);

  const isReseller = isPlatformAdminUser();

  const initializeAndConnect = useCallback(async () => {
    if (hasHandledInit.current) return;

    if (isReseller && !selectedCompany) {
      const storeState = useVehiclesSocketStore.getState();
      if (!storeState.isInitialized) {
        setAwaitingCompanySelection(true);
      }
      return;
    }

    const targetCompanyId = isReseller ? selectedCompany : companyId;

    if (!isReseller && !companyId) {
      return;
    }

    // Check if the store already has data for this company (reconnect scenario)
    const storeState = useVehiclesSocketStore.getState();
    if (storeState.isInitialized && storeState.lastInitializedCompanyId === targetCompanyId) {
      console.log("[Socket] Reconnecting to existing session (same company)");
      hasHandledInit.current = true;
      previousCompanyRef.current = targetCompanyId;

      const deviceIds = storeState.allCompanyDeviceIds;
      if (deviceIds.length > 0) {
        connect(deviceIds);
      }
      return;
    }

    // Different company than what's in the store - reset first
    if (storeState.isInitialized && storeState.lastInitializedCompanyId !== targetCompanyId) {
      console.log("[Socket] Company changed, resetting store");
      disconnect();
      resetStore();
    }

    // Full initialization
    setLoading(true);

    // Pre-connect socket while API is loading (saves ~500ms-1s)
    connectSocket();

    try {
      const devices = await fetchDevicesFromAPI(targetCompanyId || undefined);

      // Prevent double initialization (React Strict Mode fires effects twice,
      // causing two concurrent API calls - only the first to resolve should initialize)
      if (hasHandledInit.current) return;
      const currentState = useVehiclesSocketStore.getState();
      if (currentState.isInitialized && currentState.lastInitializedCompanyId === targetCompanyId) {
        hasHandledInit.current = true;
        previousCompanyRef.current = targetCompanyId;
        if (!currentState.isConnected && currentState.allCompanyDeviceIds.length > 0) {
          connect(currentState.allCompanyDeviceIds);
        }
        return;
      }

      initializeWithDevices(devices);
      useVehiclesSocketStore.setState({ lastInitializedCompanyId: targetCompanyId });

      if (devices.length === 0) {
        hasHandledInit.current = true;
        previousCompanyRef.current = targetCompanyId;
        return;
      }

      const deviceIds = devices.map((d: DeviceFromAPI) => d.id);

      connect(deviceIds);

      hasHandledInit.current = true;
      previousCompanyRef.current = targetCompanyId;
    } catch (err: unknown) {
      console.error("[Socket] Error initializing:", err);
      setLoading(false)
    }
  }, [companyId, selectedCompany, isReseller, initializeWithDevices, connect, setAwaitingCompanySelection, setLoading, disconnect, resetStore]);

  // Sync selectedCompany from companiesStore for resellers
  // When entering the app directly (page refresh/direct URL), vehiclesSocketStore.selectedCompany
  // is null but companiesStore.companyId is already initialized from localStorage.
  // This also handles company changes in DumaxAdminLayout which go through companiesStore.setCompanyId.
  useEffect(() => {
    if (!isReseller) return;
    if (companyId && companyId !== selectedCompany) {
      useVehiclesSocketStore.getState().setSelectedCompany(companyId);
    }
  }, [isReseller, companyId, selectedCompany]);

  // Main mount/unmount lifecycle
  useEffect(() => {
    initializeAndConnect();

    return () => {
      if (hasHandledInit.current) {
        // Only disconnect socket, preserve store data for fast reconnect
        disconnect();
        hasHandledInit.current = false;
      }
    };
  }, [initializeAndConnect, disconnect]);

  // Handle reseller company change
  useEffect(() => {
    if (!isReseller) return;

    if (hasHandledInit.current && selectedCompany !== previousCompanyRef.current) {
      disconnect();
      resetStore();
      hasHandledInit.current = false;
      previousGroupRef.current = "all";
      previousCompanyRef.current = selectedCompany;

      const timeoutId = setTimeout(() => {
        initializeAndConnect();
      }, 50);

      return () => clearTimeout(timeoutId);
    } else if (!hasHandledInit.current && selectedCompany) {
      // First company selection or returning from admin with new company
      setAwaitingCompanySelection(false);
      setLoading(true);

      const timeoutId = setTimeout(() => {
        initializeAndConnect();
      }, 50);

      return () => clearTimeout(timeoutId);
    }
  }, [selectedCompany, isReseller, disconnect, initializeAndConnect, resetStore, setLoading, setAwaitingCompanySelection]);

  // Handle group change
  useEffect(() => {
    if (!hasHandledInit.current) return;
    if (selectedGroup === previousGroupRef.current) return;

    previousGroupRef.current = selectedGroup;

    let deviceIdsToSubscribe: string[];

    if (selectedGroup === "all") {
      deviceIdsToSubscribe = allCompanyDeviceIds;
    } else {
      const selectedGroupData = groups.find((g) => g.id === selectedGroup);

      if (selectedGroupData && selectedGroupData.devices_ids && selectedGroupData.devices_ids.length > 0) {
        deviceIdsToSubscribe = selectedGroupData.devices_ids;
      } else {
        deviceIdsToSubscribe = allCompanyDeviceIds;
      }
    }

    if (deviceIdsToSubscribe.length > 0) {
      resubscribe(deviceIdsToSubscribe);
    }
  }, [selectedGroup, groups, allCompanyDeviceIds, resubscribe]);

  // Footprint tracking
  const footprintIdsArray = useMemo(() =>
    activeFootprintIds ? activeFootprintIds.split(',').filter(Boolean) : [],
    [activeFootprintIds]
  );

  const trackedFootprintPositions = useVehiclesSocketStore((state) => {
    if (footprintIdsArray.length === 0) return '';
    return footprintIdsArray.map(id => {
      const idx = state.vehiclesMap.get(id);
      if (idx === undefined) return '';
      const v = state.vehicles[idx];
      return `${id}:${v.latitude},${v.longitude}`;
    }).join('|');
  });

  useEffect(() => {
    if (footprintIdsArray.length === 0 || !trackedFootprintPositions) return;

    footprintIdsArray.forEach((vehicleId) => {
      const vehicle = vehiclesRef.current.find((v) => v.id === vehicleId);
      if (vehicle && vehicle.latitude && vehicle.longitude) {
        addFootprintPoint(vehicleId, vehicle.latitude, vehicle.longitude);
      }
    });
  }, [trackedFootprintPositions, footprintIdsArray, addFootprintPoint]);

  return {
    vehicles,
    loading,
    isConnected,
  };
};

export default useVehiclesSocketSubscription;